import { Calculation } from '@servicetitan/financing-api-client';

/**
 * Default copy for the notice shown above the plan list when a homeowner's estimate falls within
 * Affirm's configured range and Affirm plans are returned under a different requested provider.
 * Kept as a single exported constant so the wording is configurable in one place (and overridable
 * per call site via the PlansList `notice` slot).
 */
export const AFFIRM_RANGE_NOTICE_TEXT =
    'Based on your desired amount, only Affirm financing is available.';

const isAffirm = (providerName?: string | null): boolean =>
    providerName?.toLowerCase() === 'affirm';

export const hasAffirmCalculation = (plans: Calculation[]): boolean =>
    plans.some(plan => isAffirm(plan.providerName));

export interface AffirmRangeNoticeInput {
    /** The provider the caller originally requested (from setup data), not the returned plans. */
    requestedProvider?: string | null;
    /** Homeowner vs technician — technicians get the dedicated AffirmPaymentOptionsScreen instead. */
    role?: string | null;
    /** The plans about to be shown to the customer. */
    plans: Calculation[];
}

/**
 * True only when Affirm plans surface under a non-Affirm request in a homeowner flow — i.e. the
 * estimate landed in Affirm's range so the requested provider was swapped out. When Affirm was the
 * requested provider there's nothing to explain, and technicians never reach this list (they get
 * AffirmPaymentOptionsScreen).
 */
export const shouldShowAffirmRangeNotice = ({
    requestedProvider,
    role,
    plans,
}: AffirmRangeNoticeInput): boolean =>
    role !== 'technician' && !isAffirm(requestedProvider) && hasAffirmCalculation(plans);
